import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Typography,
  Input,
  Button,
  Tooltip,
  Popover,
  Tag,
  Space,
  message,
} from 'antd';
import { SaveOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { showToastSuccess } from '../../../../utilities/toast';
import { analytics } from 'src/services';
import { updateCurrentPractitionerSettings } from '../../../../store/actions/practitioner.action';
import { useFeatureEntitlements } from '@frontegg/react';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MAX_KEYWORD_LENGTH = 100;
const MAX_KEYWORDS = 20;

const GlobalPreferences = () => {
  const dispatch = useDispatch();
  const [instructions, setInstructions] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [currentKeywordInput, setCurrentKeywordInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [keywordError, setKeywordError] = useState('');
  const isLocalChange = useRef(false);

  const { currentPractitionerSettings } = useSelector((state) => ({
    ...state?.practitionerState,
  }));

  const isMemoryEnabled = currentPractitionerSettings?.note_preferences?.memory;

  useEffect(() => {
    // Skip the update if the change was triggered locally
    if (isLocalChange.current) {
      isLocalChange.current = false;
      return;
    }

    if (currentPractitionerSettings?.note_preferences) {
      const prefs = currentPractitionerSettings.note_preferences;
      setInstructions(prefs.custom_knowledge || '');
      setKeywords(
        Array.isArray(prefs.custom_keywords) ? prefs.custom_keywords : []
      );
    } else {
      setInstructions('');
      setKeywords([]);
    }
  }, [currentPractitionerSettings]);

  // Auto-save effect
  useEffect(() => {
    const handler = setTimeout(() => {
      if (currentPractitionerSettings && isMemoryEnabled) {
        const currentPrefs = currentPractitionerSettings.note_preferences || {};

        const hasInstructionsChanged =
          instructions !== (currentPrefs.custom_knowledge || '');
        const hasKeywordsChanged =
          JSON.stringify(keywords) !==
          JSON.stringify(currentPrefs.custom_keywords || []);

        if (hasInstructionsChanged || hasKeywordsChanged) {
          // Mark that we're about to make a local change
          isLocalChange.current = true;

          const updatedPreferences = {
            ...currentPrefs,
            custom_knowledge: instructions,
            custom_keywords: keywords,
          };
          dispatch(
            updateCurrentPractitionerSettings({
              note_preferences: updatedPreferences,
            })
          );

          if (hasInstructionsChanged) {
            analytics.track('Knowledge Base Updated In Memory Preferences', {
              content_length: instructions.length,
            });
          }

          if (hasKeywordsChanged) {
            analytics.track('Keywords Updated In Memory Preferences', {
              keyword_count: keywords.length,
              total_characters: getTotalKeywordLength(keywords),
            });
          }
        }
      }
    }, 1000); // Debounce for 1000ms

    return () => {
      clearTimeout(handler);
    };
  }, [
    instructions,
    keywords,
    dispatch,
    currentPractitionerSettings,
    isMemoryEnabled,
  ]);

  const getTotalKeywordLength = (keywordList) => {
    return keywordList.reduce((total, keyword) => total + keyword.length, 0);
  };

  const handleAddKeyword = () => {
    const newKeyword = currentKeywordInput.trim();

    if (keywords.length >= MAX_KEYWORDS) {
      setKeywordError(`Maximum of ${MAX_KEYWORDS} keywords allowed`);
      message.error(`Maximum of ${MAX_KEYWORDS} keywords allowed`);
      return;
    }

    const currentTotalLength = getTotalKeywordLength(keywords);
    if (currentTotalLength + newKeyword.length > MAX_KEYWORD_LENGTH) {
      setKeywordError(
        `Total character limit of ${MAX_KEYWORD_LENGTH} exceeded`
      );
      message.error(`Total character limit of ${MAX_KEYWORD_LENGTH} exceeded`);
      return;
    }

    if (newKeyword && !keywords.includes(newKeyword)) {
      setKeywords([...keywords, newKeyword]);
      analytics.track('Keyword Added In Memory Preferences', {
        keyword: newKeyword,
        total_keywords: keywords.length + 1,
      });
      setKeywordError('');
    }
    setCurrentKeywordInput('');
  };

  const handleRemoveKeyword = (removedKeyword) => {
    setKeywords(keywords.filter((keyword) => keyword !== removedKeyword));
    setKeywordError('');
  };

  // Calculate remaining characters
  const totalCharsUsed = getTotalKeywordLength(keywords);
  const remainingChars = 200 - totalCharsUsed;

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center">
          <Title level={4} className="m-0 mr-2">
            Global Preferences
          </Title>
          <Popover
            content={
              <Text className="text-xs">
                Provide custom instructions to guide how AI generates content
                for you. These preferences will be applied across all your
                AI-generated notes.
              </Text>
            }
            trigger="hover"
          >
            <QuestionCircleOutlined className="text-blue-500 cursor-pointer" />
          </Popover>
        </div>
      </div>
      <div className="text-black text-[18px] flex items-center">
        Knowledge Base
      </div>
      <Text type="secondary" className="mb-3 block">
        Add additional context about your specific working needs, or other
        nuances about note / code generation that templates may not cover.
      </Text>
      <TextArea
        placeholder=""
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        autoSize={{ minRows: 4, maxRows: 8 }}
        className="mb-4"
        disabled={!isMemoryEnabled}
      />
      <div className="mb-1 flex justify-between items-center">
        <div className="flex items-center">
          <div className="text-black text-[18px] flex items-center">
            Keyword detection
          </div>
          <Popover
            content={
              <Text className="text-xs">
                Provide a list of keywords or phrases (e.g., drug names,
                specific medical terms, proper nouns) that are frequently
                misspelled or misidentified during transcription. Adding them
                here helps improve accuracy.
              </Text>
            }
            trigger="hover"
          >
            <QuestionCircleOutlined className="text-blue-500 cursor-pointer ml-2" />
          </Popover>
        </div>
      </div>
      <Text type="secondary" className="mb-3 block">
        Add up to 20 keywords or phrases to boost transcription accuracy.
        <span className="ml-1">
          ({remainingChars} of 200 characters remaining)
        </span>
      </Text>

      <Space.Compact style={{ width: '100%' }} className="mb-2">
        <Input
          placeholder="E.g: Zolpidem"
          value={currentKeywordInput}
          onChange={(e) => setCurrentKeywordInput(e.target.value)}
          onPressEnter={handleAddKeyword}
          disabled={
            !isMemoryEnabled || keywords.length >= 20 || remainingChars <= 0
          }
          status={keywordError ? 'error' : ''}
        />
        <Button
          type="primary"
          onClick={handleAddKeyword}
          disabled={
            !isMemoryEnabled || keywords.length >= 20 || remainingChars <= 0
          }
        >
          Add
        </Button>
      </Space.Compact>
      {keywordError && (
        <div className="text-red-500 text-xs mb-2">{keywordError}</div>
      )}

      <div className="mb-4">
        {keywords.length > 0 ? (
          <Space wrap>
            {keywords.map((keyword) => (
              <Tag
                closable
                key={keyword}
                onClose={() => handleRemoveKeyword(keyword)}
                style={{ marginTop: '4px', marginBottom: '4px' }}
              >
                {keyword}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary" italic>
            No keywords added yet.
          </Text>
        )}
      </div>
    </>
  );
};

export default GlobalPreferences;
